/* eslint-disable react-hooks/rules-of-hooks */
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
	Box,
	Chip,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import { EndorsementExposureUpdateRequest } from '../../../types/endorsements';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import { getCharacteristic, isExposureActive } from '../../../util/util';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getExposureName } from '../util_sharedEndorsementAndRenewal';
import AddExposureModal from './Exposures/AddExposureModal';

interface Props {
	authData: AuthData;
	endorsementData: EndorsementData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * The exposure detail page for an existing exposure on an endorsement.
 */
const ExistingExposureDetails = (props: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const classes = useStyles();
	const params = useParams();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const { endorsementData, authData } = props;
	const {
		endorsementUpdateObj,
		productConfig,
		setLoading,
		policy,
		fields,
		saveFastForm,
	} = endorsementData;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [removedExposure, setRemovedExposure] = useState(null);

	const selectedExposure = endorsementData.selectedExposure as Exposure;
	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(e) => e.name === selectedExposure.name,
	);

	if (!exposureConfig) {
		return null;
	}

	// this is wrong since selectedExposure can be an ExposureCreateRequest which has no characteristics;
	const { characteristics } = selectedExposure;
	const exposureCharacteristicAtEndorsement = getCharacteristic(
		characteristics,
		endorsementUpdateObj.startTimestamp,
	);

	if (!exposureCharacteristicAtEndorsement) {
		return null;
	}

	// Parent updateExposure array
	const updateExposuresArr = endorsementUpdateObj.updateExposures;
	// exposureUpdateObj is the current ExposureUpdateRequest for the selected exposure
	let exposureUpdateObj = updateExposuresArr.find(
		(e) => e.exposureLocator === selectedExposure.locator,
	);
	exposureUpdateObj = exposureUpdateObj
		? exposureUpdateObj
		: ({} as EndorsementExposureUpdateRequest);

	const isActive = isExposureActive(
		selectedExposure,
		policy,
		endorsementUpdateObj.startTimestamp,
	);

	const removeExposure = async () => {
		try {
			setLoading(true);
			setIsConfirmModalOpen(false);
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeExposure: { exposureLocator: removedExposure.locator },
			});
			successToast('Successfully removed exposure');
			navigate(
				`/policy/${params.policyId}/endorsements/${params.endorsementId}/exposures`,
			);
		} catch (e) {
			errorToast('Error removing exposure');
		} finally {
			setLoading(false);
		}
	};

	const renderRemoveExposureModal = () => {
		return (
			<ConfirmationModal
				entityName={
					(removedExposure as Exposure).name ||
					(removedExposure as ExposureCreateRequest).exposureName
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderRemoveExposureIcon = () => {
		return (
			<Tooltip title={'Remove ' + t('exposure.singular.upper')}>
				<IconButton
					aria-label='Remove'
					className={classes.removeIcon}
					onClick={() => openConfirmationModal(selectedExposure)}
				>
					<DeleteIcon />
				</IconButton>
			</Tooltip>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		setIsConfirmModalOpen(true);
		setRemovedExposure(exposure);
	};

	const renderActions = () => {
		return (
			<AddExposureModal
				policyConfig={policyConfig}
				authData={authData}
				endorsementData={endorsementData}
			/>
		);
	};

	const defaultName = exposureConfig.displayName
		? exposureConfig.displayName
		: selectedExposure.name;
	const displayName = getExposureName(
		selectedExposure,
		defaultName,
		null,
		endorsementUpdateObj,
	);
	const isEditable =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';
	return (
		<>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={RouterLink}
						to='auxData'
					>
						Aux Data
					</MaterialLink>
				)}
				{!isActive && (
					<Chip
						color='default'
						label='Inactive'
						icon={<ErrorOutlineRoundedIcon />}
					/>
				)}
				{isEditable && renderRemoveExposureIcon()}
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</Box>
			<hr />
			<Grid container spacing={2}>
				<FastForm
					formName={selectedExposure.locator}
					form={fields}
					configVersion={productConfig.configVersion}
					authData={authData}
					productName={productConfig.name}
					disabled={!isEditable}
					save={saveFastForm}
					setLoading={setLoading}
					effectiveDate={endorsementUpdateObj.startTimestamp}
					disableExternalCalls
				/>
			</Grid>
			{isEditable && renderActions()}
		</>
	);
};

export default withEndorsementContext<Props>(ExistingExposureDetails);
